import { Box, Text } from "@vinomofo/components";

import { FONT_FAMILIES } from "../../styles";
import styled from "styled-components";
import { useCountdownTimer } from "../../hooks/use-countdown-timer";

const { dmSans, timmonsNy } = FONT_FAMILIES;

const StyledTimerContainer = styled(Box)`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin: 0;
  font-family: ${dmSans};
`;

const StyledTimerUnit = styled(Text)`
  font-family: ${dmSans};
  display: flex;
  align-items: center;
  color: #fff;
`;

const StyledTimerSeparator = styled(StyledTimerUnit)`
  margin: 0 2px;
`;

const AlternativeTimerContainer = styled(Box)`
  display: flex;
  justify-content: center;
  gap: 4px;
  margin: 0;
  @media ${({ theme }) => theme.mq.max.tablet} {
    gap: 0px;
  }
`;

const StyledAlternativeTimerUnit = styled(Box)`
  display: inline-block;
  line-height: 1;
  padding: 10px;
  font-family: ${timmonsNy};
  font-size: 98px;
  font-weight: 400;
  color: #fff;
  text-align: center;
  min-width: 80px;

  @media ${({ theme }) => theme.mq.max.tablet} {
    font-size: 90px;
    padding: 5px;
  }
`;

const StyledText = styled.span`
  font-family: ${dmSans};
  display: block;
  font-size: 16px;
  font-weight: normal;
  margin-top: -15px;
  text-align: center;
`;

const formatTimeValue = (value: number): string =>
  value.toString().padStart(2, "0");

const TimerSeparator = () => (
  <StyledTimerSeparator
    fontWeight="700"
    fontSize={["16px", "20px", "25px"]}
    lineHeight={["100%", "132%", "132%"]}
    letterSpacing={"-1px"}
  >
    :
  </StyledTimerSeparator>
);

const TimerUnit = ({ value }: { value: number }) => (
  <StyledTimerUnit
    fontWeight="700"
    fontSize={["16px", "20px", "25px"]}
    lineHeight={["100%", "132%", "132%"]}
    letterSpacing={"-1px"}
  >
    {formatTimeValue(value)}
  </StyledTimerUnit>
);

const AlternativeTimerUnit = ({
  value,
  label,
}: {
  value: number;
  label: string;
}) => {
  return (
    <StyledAlternativeTimerUnit letterSpacing={"normal"}>
      {formatTimeValue(value)}
      <StyledText>{label}</StyledText>
    </StyledAlternativeTimerUnit>
  );
};

interface TimerDisplayProps {
  dateTime?: string;
  isExpandedDisplay?: boolean;
}

const TimerDisplay = ({
  dateTime,
  isExpandedDisplay = false,
}: TimerDisplayProps) => {
  const timeLeft = useCountdownTimer(dateTime || "");
  if (isExpandedDisplay) {
    return (
      <AlternativeTimerContainer>
        <AlternativeTimerUnit value={timeLeft?.days || 0} label="DAYS" />
        <AlternativeTimerUnit value={timeLeft?.hours || 0} label="HRS" />
        <AlternativeTimerUnit value={timeLeft?.minutes || 0} label="MIN" />
        <AlternativeTimerUnit value={timeLeft?.seconds || 0} label="SEC" />
      </AlternativeTimerContainer>
    );
  }

  return (
    <StyledTimerContainer>
      <TimerUnit value={timeLeft?.days || 0} />
      <TimerSeparator />
      <TimerUnit value={timeLeft?.hours || 0} />
      <TimerSeparator />
      <TimerUnit value={timeLeft?.minutes || 0} />
      <TimerSeparator />
      <TimerUnit value={timeLeft?.seconds || 0} />
    </StyledTimerContainer>
  );
};

export { TimerDisplay };
